import {
  AppBar,
  CircularProgress,
  Dialog,
  IconButton,
  Toolbar,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Color } from '@superdispatch/ui';
import { Box, Button, TextBox } from '@superdispatch/ui-lab';
import { DateTime } from 'luxon';
import {
  printHTMLContent,
  usePrintHTMLContent,
} from 'shared/hooks/usePrintHTMLContent';
import { PrinterIcon } from 'shared/icons/PrinterIcon';
import styled from 'styled-components';
import {
  LoadInvoicePreviewParams,
  useGetCombinedInvoicePDF,
} from '../data/LoadsAPI';
import { useLoadsPageContext } from '../loads-page/data/LoadsPageParamsContext';

export const PreviewBox = styled.div`
  .page {
    background: ${Color.White};
    color: ${Color.Dark500};
  }
`;

export function formatInvoicePreviewDate(
  invoiceDate: string | null | undefined,
) {
  return DateTime.fromISO(invoiceDate || '').toFormat("yyyy-MM-dd'T'HH:mm:ss");
}

interface LoadInvoicePreviewDialogContentProps {
  params: LoadInvoicePreviewParams;
  onClose?: () => void;
}

function LoadInvoicePreviewDialogContent({
  params,
  onClose,
}: LoadInvoicePreviewDialogContentProps) {
  const { data, isLoading, isFetching } = useGetCombinedInvoicePDF(params);

  usePrintHTMLContent(data?.html || '');

  return (
    <>
      <AppBar position="fixed">
        <Toolbar>
          {onClose && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="close"
              onClick={onClose}
            >
              <Close />
            </IconButton>
          )}
          <TextBox variant="heading-3">Invoice Preview</TextBox>

          <Box marginLeft="auto">
            <Button
              variant="primary"
              startIcon={<PrinterIcon />}
              disabled={!data?.html}
              onClick={() => {
                if (data?.html) {
                  printHTMLContent(data.html);
                }
              }}
            >
              Save or Print PDF
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        width="21cm"
        height="100%"
        marginLeft="auto"
        marginRight="auto"
        marginTop="xxlarge"
        marginBottom="small"
        paddingTop="xxlarge"
      >
        {isLoading || isFetching || !data?.html ? (
          <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <PreviewBox
            id="invoice-preview"
            //  eslint-disable-next-line @typescript-eslint/naming-convention
            dangerouslySetInnerHTML={{ __html: data.html }}
          />
        )}
      </Box>
    </>
  );
}

interface LoadInvoicePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  params: LoadInvoicePreviewParams;
}

export function LoadInvoicePreviewDialog({
  open,
  onClose,
  params,
}: LoadInvoicePreviewDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={true}
      disableEscapeKeyDown={true}
    >
      <LoadInvoicePreviewDialogContent params={params} onClose={onClose} />
    </Dialog>
  );
}

export function LoadInvoicePreviewPage() {
  const { params: pageParams } = useLoadsPageContext();

  return (
    <Dialog open={true} fullScreen={true}>
      <LoadInvoicePreviewDialogContent
        params={{
          load_guids: pageParams.guids || '',
          invoice_number: pageParams.invoice_id ?? '',
          invoice_date: formatInvoicePreviewDate(pageParams.invoice_date),
          bol_template: pageParams.bol_template ?? 'default',
          factor_this_invoice: !!pageParams.factor_this_invoice,
          is_carrier_requested_superpay:
            pageParams.is_carrier_requested_superpay ?? undefined,
        }}
      />
    </Dialog>
  );
}
